/**
 * A realm's mascot: a small set of images the owner or model drops in, one per
 * *pose*, plus the pure rules for choosing which one to show.
 *
 * The art itself is made elsewhere and arrives later, so the system is built to
 * work with **none of it**: every lookup falls back, and the player's app draws a
 * placeholder when a realm has set nothing. Adding a pose later is one entry in
 * {@link MASCOT_POSES} and one row in the client's state table — no schema change,
 * since a set is just a map from pose to image.
 */

/**
 * The slots a realm may fill. A closed list on purpose, like the themes: it keeps
 * the console's uploader finite and stops a request inventing file names.
 *
 * `card` is not a pose — it is the cover art of the scratch card. It lives in the
 * same set so that one uploader, one storage layout and one fallback rule serve
 * every piece of per-realm art, rather than a second system beside this one.
 */
export const MASCOT_POSES = [
  'idle',
  'blink',
  'wave',
  'celebrate',
  'jackpot',
  'sad',
  'card',
] as const;

export type MascotPose = (typeof MASCOT_POSES)[number];

export function isMascotPose(value: unknown): value is MascotPose {
  return typeof value === 'string' && (MASCOT_POSES as readonly string[]).includes(value);
}

/**
 * What to show when a pose has no art, most specific first. Ends at `idle`, and
 * `idle` itself ends at nothing — which is the cue to draw the placeholder.
 *
 * `blink` and `card` have no fallback to a face: a blink frame that is missing
 * simply means the mascot does not blink, and a missing card cover means the
 * placeholder cover, never the mascot's face on a card.
 */
export const POSE_FALLBACKS: Record<MascotPose, MascotPose[]> = {
  idle: [],
  blink: [],
  wave: ['idle'],
  celebrate: ['wave', 'idle'],
  jackpot: ['celebrate', 'wave', 'idle'],
  sad: ['idle'],
  card: [],
};

/**
 * What is stored for one slot: an external `https` image, or an uploaded file
 * named `upload:<file>` (the file lives in the server's data directory and is
 * served from this same origin — see `routes/mascot.ts`).
 */
export type MascotSource = string;

/** A realm's mascot as it is saved in the registry. */
export interface MascotSet {
  poses: Partial<Record<MascotPose, MascotSource>>;
}

/** A realm's mascot as a player may receive it: every source already a loadable URL. */
export interface PublicMascot {
  poses: Partial<Record<MascotPose, string>>;
}

/** An uploaded file name: pose, a version stamp, a known image extension. */
export const MASCOT_FILE =
  /^(idle|blink|wave|celebrate|jackpot|sad|card)-[a-z0-9]{1,12}\.(png|webp|jpg)$/;

const UPLOAD_PREFIX = 'upload:';

/** The longest external URL accepted — the same ceiling as a hand-set avatar. */
const MAX_URL = 400;

export function uploadSource(file: string): MascotSource {
  return `${UPLOAD_PREFIX}${file}`;
}

/** The file behind an `upload:` source, or null for anything else. */
export function uploadedFile(source: string): string | null {
  if (!source.startsWith(UPLOAD_PREFIX)) return null;
  const file = source.slice(UPLOAD_PREFIX.length);
  return MASCOT_FILE.test(file) ? file : null;
}

/** Whether a string is an external image link we will store: https, bounded. */
export function isExternalUrl(source: string): boolean {
  return /^https:\/\/\S+$/i.test(source) && source.length <= MAX_URL;
}

/**
 * Backfills a persisted mascot.
 *
 * A registry from before sets existed holds `null`; the first draft of the hook
 * held a bare string, which is read as the `idle` image. Anything that is not a
 * known pose with a valid source is dropped, and a set left with nothing becomes
 * `null` — "no mascot" has exactly one spelling.
 */
export function mascotWithDefaults(parsed: unknown): MascotSet | null {
  if (typeof parsed === 'string') {
    return parsed && isExternalUrl(parsed) ? { poses: { idle: parsed } } : null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;

  const raw = (parsed as { poses?: unknown }).poses;
  if (typeof raw !== 'object' || raw === null) return null;

  const poses: MascotSet['poses'] = {};
  for (const pose of MASCOT_POSES) {
    const source = (raw as Record<string, unknown>)[pose];
    if (typeof source !== 'string') continue;
    if (uploadedFile(source) !== null || isExternalUrl(source)) poses[pose] = source;
  }
  return Object.keys(poses).length > 0 ? { poses } : null;
}

/**
 * Turns a saved set into what a player is sent: external sources as they are,
 * uploaded ones as a path on this server. `path` builds that path from a file
 * name, so this stays free of any URL layout of its own.
 */
export function toPublicMascot(
  mascot: MascotSet | null,
  path: (file: string) => string,
): PublicMascot | null {
  if (!mascot) return null;
  const poses: PublicMascot['poses'] = {};
  for (const pose of MASCOT_POSES) {
    const source = mascot.poses[pose];
    if (!source) continue;
    const file = uploadedFile(source);
    poses[pose] = file ? path(file) : source;
  }
  return Object.keys(poses).length > 0 ? { poses } : null;
}

/**
 * The image to show for a pose, walking the fallbacks. Null means "draw the
 * placeholder": the realm has no art that can stand in for this pose.
 */
export function resolvePose(
  mascot: PublicMascot | null,
  pose: MascotPose,
): { pose: MascotPose; src: string } | null {
  if (!mascot) return null;
  for (const candidate of [pose, ...POSE_FALLBACKS[pose]]) {
    const src = mascot.poses[candidate];
    if (src) return { pose: candidate, src };
  }
  return null;
}
